import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Election, ElectionState, ElectionType, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const STATES: ElectionState[] = [
  'SCHEDULED',
  'ROLL_REVISION',
  'NOMINATIONS_OPEN',
  'NOMINATIONS_CLOSED',
  'POLLING',
  'COUNTING',
  'RESULTS_PUBLISHED',
  'CLOSED',
  'CANCELLED',
];
const COMMISSIONER_ROLES = ['ELECTORAL_COMMISSIONER'];

/** GET /api/v1/m39/elections — every authenticated T1+ persona can list; only ELECTORAL_COMMISSIONER may create. */
export function Elections() {
  const { persona } = useAuth();
  const isCommissioner = persona?.roles.some((r) => COMMISSIONER_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [status, setStatus] = useState<ElectionState | ''>('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<Election>>(`/api/v1/m39/elections?${params.toString()}`);

  return (
    <div>
      <PageHeader
        title="Elections"
        subtitle="Presidential and National Assembly election lifecycle — scheduling through results publication."
        actions={
          isCommissioner ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Schedule election
            </button>
          ) : undefined
        }
      />

      {!isCommissioner ? <RoleNotice roles={COMMISSIONER_ROLES} /> : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Title contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Presidential…" />
        </Field>
        <Field label="State" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as ElectionState | '')}>
            <option value="">All</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No elections match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Type</th>
                <th className="table-head">State</th>
                <th className="table-head">Election date</th>
                <th className="table-head">Deposit</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/${e.id}`)}>
                  <td className="table-cell font-medium">{e.title}</td>
                  <td className="table-cell text-xs">{e.type.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={e.state} />
                  </td>
                  <td className="table-cell text-xs">{formatDate(e.electionDate)}</td>
                  <td className="table-cell text-xs">{scr(e.depositAmountScr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateElectionModal
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            navigate(`/elections/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateElectionModal({ onClose, onDone }: { onClose: () => void; onDone: (e: Election) => void }) {
  const [type, setType] = useState<ElectionType>('NATIONAL_ASSEMBLY');
  const [title, setTitle] = useState('');
  const [electionDate, setElectionDate] = useState('');
  const [depositAmountScr, setDepositAmountScr] = useState('5000');
  const [depositForfeitThresholdPct, setDepositForfeitThresholdPct] = useState('12.5');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Election>('/api/v1/m39/elections', {
        type,
        title,
        electionDate,
        depositAmountScr: Number.parseFloat(depositAmountScr),
        depositForfeitThresholdPct: Number.parseFloat(depositForfeitThresholdPct),
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to schedule election');
    } finally {
      setBusy(false);
    }
  };

  const valid = title.trim().length >= 3 && /^\d{4}-\d{2}-\d{2}$/.test(electionDate) && Number.parseFloat(depositAmountScr) >= 0;

  return (
    <Modal title="Schedule an election" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as ElectionType)}>
            <option value="NATIONAL_ASSEMBLY">National Assembly</option>
            <option value="PRESIDENTIAL">Presidential</option>
          </select>
        </Field>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. 2026 National Assembly General Election" />
        </Field>
        <Field label="Election date">
          <input type="date" className="field-input" value={electionDate} onChange={(e) => setElectionDate(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nomination deposit (SCR)">
            <input type="number" className="field-input" value={depositAmountScr} onChange={(e) => setDepositAmountScr(e.target.value)} />
          </Field>
          <Field label="Deposit forfeit threshold (%)">
            <input type="number" className="field-input" value={depositForfeitThresholdPct} onChange={(e) => setDepositForfeitThresholdPct(e.target.value)} />
          </Field>
        </div>
        <p className="text-xs text-slate-500">
          A candidate whose vote share falls below the forfeit threshold loses their deposit; at or above it, the deposit is refunded.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Scheduling…' : 'Schedule election'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
